import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Key, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Save, 
  CheckCircle2, 
  XCircle, 
  Crown, 
  Lock, 
  Layers, 
  Info,
  Check,
  Search
} from 'lucide-react';
import { permissionsAPI } from '../utils/api';

const ROLE_DEFINITIONS = [
  { key: 'OWNER', label: 'Owner (Root)', badge: 'bg-amber-100 text-amber-800 border-amber-300', isSupreme: true },
  { key: 'SUPER_ADMIN', label: 'System Admin (Super Admin)', badge: 'bg-rose-100 text-rose-800 border-rose-300', isSupreme: false },
  { key: 'ADMIN', label: 'Administrator', badge: 'bg-purple-100 text-purple-800 border-purple-300', isSupreme: false },
  { key: 'MANAGER', label: 'Executive Manager', badge: 'bg-blue-100 text-blue-800 border-blue-300', isSupreme: false }
];

const 

PermissionsManagement = () => {
  const [selectedRole, setSelectedRole] = useState('SUPER_ADMIN');
  const [modules, setModules] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModuleOpen, setIsAddModuleOpen] = useState(false);
  const [newModuleData, setNewModuleData] = useState({
    module_name: '',
    module_key: '',
    category: 'CUSTOM',
    description: ''
  });
  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3500);
  };

  useEffect(() => {
    fetchMatrix();
  }, []);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const res = await permissionsAPI.getMatrix();
      if (res.data?.success) {
        setModules(res.data.modules || []);
        setMatrix(res.data.matrix || {});
      }
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Error loading permissions matrix', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePerm = (moduleKey, permType) => {
    if (selectedRole === 'OWNER') return; // Owner is immutable supreme

    setMatrix(prev => {
      const rolePerms = [...(prev[selectedRole] || [])];
      const modIndex = rolePerms.findIndex(m => m.module_key === moduleKey);
      if (modIndex !== -1) {
        rolePerms[modIndex] = {
          ...rolePerms[modIndex],
          [permType]: !rolePerms[modIndex][permType]
        };
      }
      return {
        ...prev,
        [selectedRole]: rolePerms
      };
    });
  };

  const handleToggleAllForModule = (moduleKey, enable) => {
    if (selectedRole === 'OWNER') return;

    setMatrix(prev => {
      const rolePerms = [...(prev[selectedRole] || [])];
      const modIndex = rolePerms.findIndex(m => m.module_key === moduleKey);
      if (modIndex !== -1) {
        rolePerms[modIndex] = {
          ...rolePerms[modIndex],
          can_read: enable,
          can_create: enable,
          can_update: enable,
          can_delete: enable
        };
      }
      return {
        ...prev,
        [selectedRole]: rolePerms
      };
    });
  };

  const handleBulkRoleAction = (enable) => {
    if (selectedRole === 'OWNER') return;

    setMatrix(prev => {
      const rolePerms = (prev[selectedRole] || []).map(m => ({
        ...m,
        can_read: enable,
        can_create: enable,
        can_update: enable,
        can_delete: enable
      }));
      return {
        ...prev,
        [selectedRole]: rolePerms
      };
    });
  };

  const handleSaveRolePermissions = async () => {
    if (selectedRole === 'OWNER') {
      showToast('Owner has supreme permissions across all modules by default.', 'info');
      return;
    }

    setSaving(true);
    try {
      const rolePerms = matrix[selectedRole] || [];
      const payload = {
        permissions: rolePerms.map(m => ({
          module_key: m.module_key,
          can_read: Boolean(m.can_read),
          can_create: Boolean(m.can_create),
          can_update: Boolean(m.can_update),
          can_delete: Boolean(m.can_delete)
        }))
      };

      const res = await permissionsAPI.updateRolePermissions(selectedRole, payload);
      showToast(res.data?.message || `Permissions for ${selectedRole} saved successfully!`);
      fetchMatrix();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving permissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    try {
      if (!newModuleData.module_name.trim() || !newModuleData.module_key.trim()) {
        showToast('Please provide both module name and unique key', 'error');
        return;
      }

      const res = await permissionsAPI.createModule(newModuleData);
      showToast(res.data?.message || 'Dynamic module added successfully!');
      setIsAddModuleOpen(false);
      setNewModuleData({ module_name: '', module_key: '', category: 'CUSTOM', description: '' });
      fetchMatrix();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error creating module', 'error');
    }
  };

  const handleDeleteModule = async (moduleKey, moduleName) => {
    if (!window.confirm(`Are you sure you want to permanently delete custom module "${moduleName}" (${moduleKey})?`)) {
      return;
    }
    try {
      const res = await permissionsAPI.deleteModule(moduleKey);
      showToast(res.data?.message || 'Module removed successfully');
      fetchMatrix();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error deleting module', 'error');
    }
  };

  const currentRolePerms = matrix[selectedRole] || [];
  const filteredPerms = currentRolePerms.filter(m => {
    const term = searchTerm.toLowerCase();
    return (m.module_name || '').toLowerCase().includes(term) ||
           (m.module_key || '').toLowerCase().includes(term) ||
           (m.category || '').toLowerCase().includes(term) ||
           (m.description || '').toLowerCase().includes(term);
  });

  const isCurrentRoleSupreme = selectedRole === 'OWNER';

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-3xl font-extrabold text-slate-800">Permission Management</h2>
            <span className="bg-pink-100 text-pink-700 text-xs px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Dynamic Matrix
            </span>
          </div>
          <p className="text-slate-500 mt-1">
            Configure granular CRUD operations (Create, Read, Update, Delete) per module across staff roles.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <button 
            onClick={fetchMatrix}
            className="flex items-center gap-1.5 bg-white text-slate-700 px-4 py-2.5 text-sm rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition active:scale-95 cursor-pointer"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={() => setIsAddModuleOpen(true)}
            className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2.5 text-sm rounded-xl font-semibold shadow-lg shadow-purple-500/20 hover:brightness-110 transition active:scale-95 cursor-pointer"
          >
            <Plus size={16} /> Add New Module
          </button>
          {!isCurrentRoleSupreme && (
            <button
              onClick={handleSaveRolePermissions}
              disabled={saving}
              className="flex items-center gap-1.5 bg-pink-600 text-white px-5 py-2.5 text-sm rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition active:scale-95 cursor-pointer disabled:opacity-50"
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          )}
        </div>
      </div>

      {/* Role Selection Tabs */}
      <div className="bg-white rounded-2xl p-2 shadow-sm border border-slate-100 flex flex-wrap gap-2">
        {ROLE_DEFINITIONS.map(r => (
          <button
            key={r.key}
            onClick={() => setSelectedRole(r.key)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer ${
              selectedRole === r.key
                ? 'bg-slate-900 text-white shadow-md shadow-slate-900/20 scale-[1.02]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {r.isSupreme ? <Crown size={16} className="text-amber-400" /> : <Key size={15} />}
            {r.label}
            {r.isSupreme && (
              <span className="text-[10px] bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded uppercase font-black">
                Supreme
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Role Info & Quick Toolbar */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl border ${ROLE_DEFINITIONS.find(r => r.key === selectedRole)?.badge}`}>
            {isCurrentRoleSupreme ? <Crown size={24} /> : <ShieldCheck size={24} />}
          </div>
          <div>
            <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
              Permissions for {ROLE_DEFINITIONS.find(r => r.key === selectedRole)?.label}
              {isCurrentRoleSupreme && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">
                  All Rights Granted Automatically
                </span>
              )}
            </h3>
            <p className="text-slate-500 text-xs mt-0.5">
              {isCurrentRoleSupreme
                ? 'The OWNER account holds absolute authority over the entire ecosystem. Permissions cannot be restricted.'
                : selectedRole === 'SUPER_ADMIN'
                ? 'System Admin has platform-wide authority. You can customize CRUD privileges per module below.'
                : 'Manage what this role is allowed to view, create, edit, or delete within each system module.'}
            </p>
          </div>
        </div>

        {!isCurrentRoleSupreme && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBulkRoleAction(true)}
              className="px-3 py-1.5 text-xs font-bold bg-green-50 text-green-700 hover:bg-green-100 rounded-lg border border-green-200 transition active:scale-95"
            >
              Select All
            </button>
            <button
              onClick={() => handleBulkRoleAction(false)}
              className="px-3 py-1.5 text-xs font-bold bg-slate-50 text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200 transition active:scale-95"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          type="text"
          placeholder="Filter modules..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20"
        />
      </div>

      {/* Module Matrix */}
      {loading ? (
        <div className="p-20 text-center bg-white rounded-2xl border border-slate-100">
          <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading dynamic modules matrix...</p>
        </div>
      ) : (
        <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70 text-slate-500 text-xs font-bold uppercase tracking-wider border-b border-slate-100">
                  <th className="p-5">Module / Feature</th>
                  <th className="p-5 text-center">👁️ Read (View)</th>
                  <th className="p-5 text-center">➕ Create (Add)</th>
                  <th className="p-5 text-center">✏️ Update (Edit)</th>
                  <th className="p-5 text-center">🗑️ Delete (Remove)</th>
                  {!isCurrentRoleSupreme && <th className="p-5 text-right">Quick Toggles</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredPerms.map((mod) => (
                  <tr key={mod.module_key} className="hover:bg-slate-50/50 transition duration-150">
                    <td className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-pink-50 text-pink-600 mt-0.5">
                          <Layers size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 text-sm">{mod.module_name}</span>
                            <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                              {mod.category || 'CORE'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{mod.description || 'System module operations'}</p>
                          <span className="text-[10px] text-slate-400 font-mono">key: {mod.module_key}</span>
                        </div>
                      </div>
                    </td>

                    {/* READ */}
                    <td className="p-5 text-center">
                      <button
                        type="button"
                        disabled={isCurrentRoleSupreme}
                        onClick={() => handleTogglePerm(mod.module_key, 'can_read')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer ${
                          mod.can_read
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-300'
                            : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {mod.can_read ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                        {mod.can_read ? 'Allowed' : 'Denied'}
                      </button>
                    </td>

                    {/* CREATE */}
                    <td className="p-5 text-center">
                      <button
                        type="button"
                        disabled={isCurrentRoleSupreme}
                        onClick={() => handleTogglePerm(mod.module_key, 'can_create')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer ${
                          mod.can_create
                            ? 'bg-blue-50 text-blue-700 border border-blue-300'
                            : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {mod.can_create ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                        {mod.can_create ? 'Allowed' : 'Denied'}
                      </button>
                    </td>

                    {/* UPDATE */}
                    <td className="p-5 text-center">
                      <button
                        type="button"
                        disabled={isCurrentRoleSupreme}
                        onClick={() => handleTogglePerm(mod.module_key, 'can_update')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer ${
                          mod.can_update
                            ? 'bg-amber-50 text-amber-700 border border-amber-300'
                            : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {mod.can_update ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                        {mod.can_update ? 'Allowed' : 'Denied'}
                      </button>
                    </td>

                    {/* DELETE */}
                    <td className="p-5 text-center">
                      <button
                        type="button"
                        disabled={isCurrentRoleSupreme}
                        onClick={() => handleTogglePerm(mod.module_key, 'can_delete')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer ${
                          mod.can_delete
                            ? 'bg-rose-50 text-rose-700 border border-rose-300'
                            : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {mod.can_delete ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                        {mod.can_delete ? 'Allowed' : 'Denied'}
                      </button>
                    </td>

                    {/* QUICK TOGGLE PER MODULE */}
                    {!isCurrentRoleSupreme && (
                      <td className="p-5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleToggleAllForModule(mod.module_key, true)}
                            title="Grant all 4 operations"
                            className="px-2 py-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md border border-emerald-200"
                          >
                            All
                          </button>
                          <button
                            onClick={() => handleToggleAllForModule(mod.module_key, false)}
                            title="Revoke all operations"
                            className="px-2 py-1 text-[11px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-md border border-rose-200"
                          >
                            None
                          </button>
                          {mod.category === 'CUSTOM' && (
                            <button
                              onClick={() => handleDeleteModule(mod.module_key, mod.module_name)}
                              title="Delete this dynamic module"
                              className="p-1 text-slate-400 hover:text-red-600 rounded-md transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}

                {filteredPerms.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-12 text-center text-slate-400">
                      No modules found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Save Button Bar (Fixed at bottom for easy access) */}
      {!isCurrentRoleSupreme && (
        <div className="sticky bottom-4 z-40 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-slate-200 flex items-center justify-between">
          <div className="text-xs text-slate-500 font-semibold flex items-center gap-1.5">
            <Info size={16} className="text-pink-600" />
            Changes made to <strong>{selectedRole}</strong> will apply immediately upon saving.
          </div>
          <button
            onClick={handleSaveRolePermissions}
            disabled={saving}
            className="flex items-center gap-2 bg-pink-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition active:scale-95 cursor-pointer disabled:opacity-50"
          >
            <Save size={16} /> {saving ? 'Saving...' : `Save ${selectedRole} Permissions`}
          </button>
        </div>
      )}

      {/* Modal: Add New Dynamic Module */}
      {isAddModuleOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Plus size={20} className="text-pink-600" />
                Define New System Module
              </h3>
              <button 
                onClick={() => setIsAddModuleOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateModule} className="space-y-4 pt-4">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Module Name</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. Tournament Hub, Audio Filters, VIP Center"
                  value={newModuleData.module_name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const autoKey = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
                    setNewModuleData(prev => ({
                      ...prev,
                      module_name: name,
                      module_key: prev.module_key === '' || prev.module_key === autoKey.slice(0, -1) ? autoKey : prev.module_key
                    }));
                  }}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Unique Module Key (Slug)</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. tournament_hub"
                  value={newModuleData.module_key}
                  onChange={(e) => setNewModuleData({ ...newModuleData, module_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-pink-500/20"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Category</label>
                <select
                  value={newModuleData.category}
                  onChange={(e) => setNewModuleData({ ...newModuleData, category: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 font-bold"
                >
                  <option value="CUSTOM">CUSTOM MODULE</option>
                  <option value="CORE">CORE SYSTEM</option>
                  <option value="BUSINESS">BUSINESS & MONETIZATION</option>
                  <option value="MANAGEMENT">STAFF & PORTFOLIO</option>
                  <option value="FINANCE">FINANCIAL LEDGER</option>
                  <option value="STREAMING">LIVE & INTERACTION</option>
                  <option value="SECURITY">MODERATION & TRUST</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Description (Optional)</label>
                <textarea
                  rows="2"
                  placeholder="Describe what operations this module encompasses..."
                  value={newModuleData.description}
                  onChange={(e) => setNewModuleData({ ...newModuleData, description: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddModuleOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-pink-600 text-white font-bold text-sm hover:bg-pink-700 shadow-lg shadow-pink-500/30"
                >
                  Create Module
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${
          toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' :
          toast.type === 'info' ? 'bg-blue-50 text-blue-700 border-blue-100' :
          'bg-green-50 text-green-700 border-green-100'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default PermissionsManagement;
